import * as React from "react";
import {
  DataTable,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { configProps, Driver, Dropdowns } from "../../../types";
import { Column } from "primereact/column";
import { useRef, useState } from "react";
import AddNewDriver from "./AddNewDriver";
import { mapLabelsToIds } from "../../../hooks/mapLabelsToIds";
import DeleteDriver from "./DeleteDriver";
import { useAppDispatch } from "../../../redux/store";
import { createFormSlice } from "../../../redux/slices/createForm";
import { Button } from "primereact/button";
import { HistoryDialog } from "../../../components/HistoryDialog";
import { CommentsDialog } from "../../../components/CommentsDialog";

type DriversTableProps = {
  config: configProps<"drivers">;
  data: Driver[];
  visibleColumns: configProps<"drivers">["columns"];
  isLoading: boolean;
  globalFilterValue: string;
  // filters: DataTableFilterMeta;
  dropdownConfigs: Pick<
    Dropdowns,
    "trucks" | "trailers" | "transporters" | "organizationalUsers"
  >;
  page: number;
  rowsPerPage: number;
  onPage: (e: DataTablePageEvent) => void;
  totalRecords?: number;
  sortField: string;
  sortOrder: number;
  onSort: (e: DataTableSortEvent) => void;
};

export const DriversTable: React.FC<DriversTableProps> = ({
  config,
  data,
  visibleColumns,
  isLoading,
  globalFilterValue,
  // filters,
  dropdownConfigs,
  page,
  rowsPerPage,
  onPage,
  totalRecords,
  sortField,
  sortOrder,
  onSort,
}) => {
  const driversTableRef = useRef<DataTable<Driver[]>>(null);

  const dispatch = useAppDispatch();

  const [selectedHistoryRow, setSelectedHistoryRow] = useState<Driver | null>(
    null,
  );
  const [selectedCommentsRow, setSelectedCommentsRow] = useState<Driver | null>(
    null,
  );

  const newClickBody = React.useCallback(
    (rowData: Driver) => {
      return (
        <div className="flex-container">
          <Button
            size="small"
            icon="pi pi-pencil"
            text
            onClick={() => {
              dispatch(
                createFormSlice.actions.openForm({
                  mode: "edit",
                  initialValues: {
                    firstName: rowData.firstName,
                    lastName: rowData.lastName,
                    personalCode: rowData.personalCode,
                    phone: rowData.phone,
                    email: rowData.email,
                    companyId: rowData.companyId,
                    managerId: rowData.managerId,
                    trucksIds: mapLabelsToIds(
                      rowData.trucks,
                      dropdownConfigs.trucks,
                    ),
                    trailersIds: mapLabelsToIds(
                      rowData.trailers,
                      dropdownConfigs.trailers,
                    ),
                    driverId: rowData.id,
                  },
                }),
              );
            }}
          />
          <Button
            size="small"
            icon="pi pi-history"
            text
            onClick={(e) => {
              e.stopPropagation();
              setSelectedHistoryRow(rowData);
            }}
          />
          <Button
            size="small"
            className="wide"
            icon="pi pi-comment"
            text={rowData.commentsCount === 0}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCommentsRow(rowData);
            }}
          />
          <DeleteDriver rowData={rowData} />
        </div>
      );
    },

    [dispatch, dropdownConfigs.trailers, dropdownConfigs.trucks],
  );

  return (
    <div>
      <DataTable
        ref={driversTableRef}
        value={data}
        dataKey="id"
        size="small"
        lazy
        paginator
        rows={rowsPerPage}
        first={page * rowsPerPage}
        totalRecords={totalRecords}
        onPage={onPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sortField={sortField}
        sortOrder={(sortOrder ?? 1) as 1 | -1}
        onSort={onSort}
        globalFilter={globalFilterValue}
        // filters={filters}
        scrollable
        scrollHeight="75vh"
        emptyMessage={
          isLoading ? "Ładowanie danych..." : "Nie znaleziono danych!"
        }
      >
        {visibleColumns
          .filter((entry) => entry !== undefined)
          .map((entry) => {
            return (
              <Column
                key={entry.field}
                field={entry.field}
                header={entry.header}
                sortable={entry.sortable ?? false}
                body={entry.body}
              />
            );
          })}
        <Column className="icon-column" body={newClickBody} />
      </DataTable>
      <HistoryDialog<"drivers">
        rowData={selectedHistoryRow}
        onHide={() => setSelectedHistoryRow(null)}
        columns={config.columns}
      />
      <CommentsDialog<"drivers">
        rowData={selectedCommentsRow}
        onHide={() => setSelectedCommentsRow(null)}
        columns={config.columns}
      />
      <AddNewDriver
        columns={config.columns}
        dropdownConfigs={dropdownConfigs}
        inputContext="driverTable"
      />
    </div>
  );
};
